/* ============================================
   Wiki — Main MAPRO Wiki Page
   Layout: WikiSidebar | Content | AnnotationsPanel
   Modes: browse categories, view article, edit
   ============================================ */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useWiki } from '@/hooks/useWiki'
import { useWikiAnnotations } from '@/hooks/useWikiAnnotations'
import WikiSidebar from '@/components/wiki/WikiSidebar'
import WikiArticleViewer from '@/components/wiki/WikiArticleViewer'
import WikiArticleEditor from '@/components/wiki/WikiArticleEditor'
import AnnotationLayer from '@/components/wiki/AnnotationLayer'
import AnnotationsPanel from '@/components/wiki/AnnotationsPanel'
import WikiComments from '@/components/wiki/WikiComments'
import type { WikiArticulo } from '@/hooks/useWiki'

/* ── Inline SVG Icons ── */
const icons = {
  book: (s = 24) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
  ),
  plus: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  sidebar: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  ),
  panelRight: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  ),
  folder: (s = 40) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
    </svg>
  ),
  fileText: (s = 40) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  search: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
}

export default function Wiki() {
  const { categoriaSlug, articuloSlug } = useParams<{
    categoriaSlug?: string
    articuloSlug?: string
  }>()
  const navigate = useNavigate()

  /*
    Quién puede escribir. El candado de verdad está en el backend
    (`wiki.routes.ts`: las altas y bajas de artículos y categorías son
    `adminOnly`); esto sólo evita dibujar botones que iban a devolver 403.
    Comentar y anotar sí lo puede hacer cualquiera de la casa.
  */
  const { isAdmin } = useAuth()

  const {
    categorias,
    articuloActual,
    loading: wikiLoading,
    loadCategorias,
    loadArticulo,
    buscar,
    crearArticulo,
    actualizarArticulo,
    eliminarArticulo,
    setArticuloActual,
  } = useWiki()

  const {
    anotaciones,
    loadAnotaciones,
    crearAnotacion,
    eliminarAnotacion,
  } = useWikiAnnotations()

  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<WikiArticulo[]>([])
  const [isEditing, setIsEditing] = useState(false)
  const [showSidebar, setShowSidebar] = useState(true)
  const [showAnnotations, setShowAnnotations] = useState(true)
  const contentRef = useRef<HTMLDivElement>(null)

  /* ── Load categories on mount ── */
  useEffect(() => {
    loadCategorias()
  }, [loadCategorias])

  /* ── Load article when URL changes ── */
  useEffect(() => {
    if (categoriaSlug && articuloSlug) {
      loadArticulo(categoriaSlug, articuloSlug)
      setIsEditing(false)
    } else {
      setArticuloActual(null)
    }
  }, [categoriaSlug, articuloSlug, loadArticulo, setArticuloActual])

  /* ── Load annotations when article changes ── */
  useEffect(() => {
    if (articuloActual?.id) {
      loadAnotaciones(articuloActual.id)
    }
  }, [articuloActual?.id, loadAnotaciones])

  /* ── Search debounced ── */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    const timeout = setTimeout(async () => {
      const results = await buscar(searchQuery)
      setSearchResults(results)
    }, 300)

    return () => clearTimeout(timeout)
  }, [searchQuery, buscar])

  /* ── Navigation handlers ── */
  const handleSelectArticulo = useCallback((catSlug: string, artSlug: string) => {
    navigate(`/wiki/${catSlug}/${artSlug}`)
    setSearchQuery('')
    setSearchResults([])
  }, [navigate])

  const handleSelectCategoria = useCallback((_catSlug: string) => {
    // Could navigate to category view in the future
  }, [])

  /* ── Scroll to annotation ── */
  const handleClickAnnotation = useCallback((id: string) => {
    const el = document.querySelector(`mark[data-annotation-id="${id}"]`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Flash effect
      const mark = el as HTMLElement
      mark.style.transition = 'background-color 0.3s'
      const originalBg = mark.style.backgroundColor
      mark.style.backgroundColor = 'color-mix(in srgb, var(--primary-light) 40%, transparent)'
      setTimeout(() => {
        mark.style.backgroundColor = originalBg
      }, 1500)
    }
  }, [])

  /* ── Save article (create or update) ── */
  const handleSaveArticle = useCallback(async (data: {
    titulo: string
    slug: string
    contenido: string
    resumen: string | null
    categoria_id: string
    estado: 'borrador' | 'publicado' | 'archivado'
  }) => {
    if (articuloActual) {
      // Update existing
      const updated = await actualizarArticulo(articuloActual.id, data)
      if (updated) {
        setIsEditing(false)
        // Find category slug for navigation
        const cat = categorias.find(c => c.id === data.categoria_id)
        if (cat) {
          navigate(`/wiki/${cat.slug}/${data.slug}`)
        }
        loadCategorias()
      }
    } else {
      // Create new
      const created = await crearArticulo({
        ...data,
        orden: 0,
        metadata: {},
      })
      if (created) {
        setIsEditing(false)
        const cat = categorias.find(c => c.id === data.categoria_id)
        if (cat) {
          navigate(`/wiki/${cat.slug}/${data.slug}`)
        }
        loadCategorias()
      }
    }
  }, [articuloActual, actualizarArticulo, crearArticulo, categorias, navigate, loadCategorias])

  /* ── Delete article ── */
  const handleDeleteArticle = useCallback(async () => {
    if (!articuloActual) return
    if (!confirm(`¿Eliminar "${articuloActual.titulo}"? Esta acción no se puede deshacer.`)) return

    const success = await eliminarArticulo(articuloActual.id)
    if (success) {
      navigate('/wiki')
      loadCategorias()
    }
  }, [articuloActual, eliminarArticulo, navigate, loadCategorias])

  /* ── Create annotation ── */
  const handleCreateAnnotation = useCallback(async (data: {
    texto_seleccionado: string
    selector_start: string
    offset_start: number
    selector_end: string
    offset_end: number
    nota: string | null
    color: string
  }) => {
    if (!articuloActual) return
    await crearAnotacion({
      articulo_id: articuloActual.id,
      ...data,
    })
  }, [articuloActual, crearAnotacion])

  /* ── Render welcome/home view ── */
  const renderHome = () => (
    <div className="wiki-home">
      <div className="wiki-home__header">
        <h1 className="wiki-home__title">
          {icons.book(32)} Manual de Procesos
        </h1>
        <p className="wiki-home__subtitle">
          Documentación operativa, procedimientos y guías de trabajo
        </p>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="wiki-home__search-results">
          <h3 className="wiki-home__section-title">
            {icons.search(16)} Resultados para "{searchQuery}"
          </h3>
          <div className="wiki-home__results-list">
            {searchResults.map(art => (
              <button
                key={art.id}
                className="wiki-home__result-item"
                onClick={() => {
                  if (art.categoria) {
                    handleSelectArticulo(art.categoria.slug, art.slug)
                  }
                }}
              >
                <span className="wiki-home__result-title">{art.titulo}</span>
                {art.resumen && (
                  <span className="wiki-home__result-summary">{art.resumen}</span>
                )}
                {art.categoria && (
                  <span className="wiki-home__result-cat">{art.categoria.nombre}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category Cards */}
      {!searchQuery && (
        <div className="wiki-home__categories">
          {categorias.map(cat => (
            <div
              key={cat.id}
              className="wiki-home__category-card"
              onClick={() => {
                if (cat.articulos.length > 0) {
                  handleSelectArticulo(cat.slug, cat.articulos[0].slug)
                }
              }}
              style={{ cursor: cat.articulos.length > 0 ? 'pointer' : 'default' }}
            >
              <div
                className="wiki-home__category-icon"
                style={{ color: cat.color || 'var(--primary-light)' }}
              >
                {icons.folder(40)}
              </div>
              <h3 className="wiki-home__category-name">{cat.nombre}</h3>
              {cat.descripcion && (
                <p className="wiki-home__category-desc">{cat.descripcion}</p>
              )}
              <span className="wiki-home__category-count">
                {cat.articulos_count} {cat.articulos_count === 1 ? 'artículo' : 'artículos'}
              </span>
            </div>
          ))}

          {categorias.length === 0 && !wikiLoading && (
            <div className="wiki-home__empty">
              <div className="wiki-home__empty-icon">{icons.fileText(48)}</div>
              <h3>No hay categorías</h3>
              <p>
                {isAdmin
                  ? 'Crea tu primera categoría y artículo para comenzar'
                  : 'Todavía no hay nada publicado acá.'}
              </p>
              {isAdmin && (
                <button
                  className="btn btn-primary"
                  onClick={() => setIsEditing(true)}
                >
                  {icons.plus(16)} Nuevo Artículo
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )

  /* ── Render article view ── */
  const renderArticleView = () => {
    if (!articuloActual) return null

    return (
      <div className="wiki-content__article" ref={contentRef} style={{ position: 'relative' }}>
        <WikiArticleViewer
          articulo={articuloActual}
          onEdit={isAdmin ? () => setIsEditing(true) : undefined}
          onDelete={isAdmin ? handleDeleteArticle : undefined}
        />

        <AnnotationLayer
          articuloId={articuloActual.id}
          containerRef={contentRef}
          anotaciones={anotaciones}
          onCreateAnnotation={handleCreateAnnotation}
          onDeleteAnnotation={eliminarAnotacion}
        />

        <WikiComments articuloId={articuloActual.id} />
      </div>
    )
  }

  return (
    <div className="wiki-page">
      {/* ── Top Action Bar ── */}
      <div className="wiki-page__topbar">
        <div className="wiki-page__topbar-left">
          <button
            className="wiki-page__toggle-btn"
            onClick={() => setShowSidebar(!showSidebar)}
            title={showSidebar ? 'Ocultar sidebar' : 'Mostrar sidebar'}
          >
            {icons.sidebar()}
          </button>
          <h1 className="wiki-page__title">{icons.book(20)} Wiki MAPRO</h1>
        </div>
        <div className="wiki-page__topbar-right">
          {isAdmin && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setArticuloActual(null)
                setIsEditing(true)
              }}
            >
              {icons.plus(14)} Nuevo Artículo
            </button>
          )}
          {articuloActual && (
            <button
              className="wiki-page__toggle-btn"
              onClick={() => setShowAnnotations(!showAnnotations)}
              title={showAnnotations ? 'Ocultar anotaciones' : 'Mostrar anotaciones'}
            >
              {icons.panelRight()}
            </button>
          )}
        </div>
      </div>

      {/* ── Main Layout ── */}
      <div className="wiki-page__layout">
        {/* Sidebar */}
        {showSidebar && (
          <WikiSidebar
            categorias={categorias}
            articuloActual={articuloActual}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelectArticulo={handleSelectArticulo}
            onSelectCategoria={handleSelectCategoria}
          />
        )}

        {/* Content Area */}
        <div className="wiki-content">
          {wikiLoading && !articuloActual && (
            <div className="wiki-content__loading">
              Cargando...
            </div>
          )}

          {isEditing ? (
            <WikiArticleEditor
              articulo={articuloActual}
              categorias={categorias}
              onSave={handleSaveArticle}
              onCancel={() => setIsEditing(false)}
            />
          ) : articuloActual ? (
            renderArticleView()
          ) : (
            renderHome()
          )}
        </div>

        {/* Annotations Panel */}
        {articuloActual && showAnnotations && !isEditing && (
          <AnnotationsPanel
            anotaciones={anotaciones}
            onClickAnnotation={handleClickAnnotation}
            onDeleteAnnotation={eliminarAnotacion}
          />
        )}
      </div>
    </div>
  )
}
